import React, { useCallback, useMemo } from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { detailCells, DetailGridTextCell, DetailHierarchySpacerCell } from "./bankRecGridHelpers";
import { buildGroupedDisplayRows } from "./bankRecGrouping";
import styles from "./bankrec.module.scss";

const kendoCheckboxInputClass = "k-checkbox k-checkbox-md k-rounded-md";

function GroupSelectCheckboxTd({ groupAllSelected, groupSomeSelected, groupChildren, setSelectedState }) {
    return (
        <td className="text-center">
            <span className="k-checkbox-wrap d-inline-flex justify-content-center">
                <input
                    type="checkbox"
                    className={kendoCheckboxInputClass}
                    tabIndex={-1}
                    checked={groupAllSelected}
                    ref={(el) => {
                        if (el) {
                            el.indeterminate = !groupAllSelected && groupSomeSelected;
                        }
                    }}
                    onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedState((prev) => {
                            const next = { ...prev };
                            groupChildren.forEach((child) => {
                                next[child.postingId] = checked;
                            });
                            return next;
                        });
                    }}
                />
            </span>
        </td>
    );
}

export function useBankRecExpandedGrid({
    dataResultData,
    expandedGroups,
    selectedState,
    setSelectedState,
    selectField,
    DateCell,
    MatterRefCell,
    RecieptCell,
    PaymentCell,
}) {
    const displayedGridData = useMemo(
        () => buildGroupedDisplayRows(dataResultData || [], expandedGroups, selectedState),
        [dataResultData, expandedGroups, selectedState],
    );

    const SelectCell = useCallback(
        (props) => {
            if (props.dataItem?.isGroupParent) {
                return (
                    <GroupSelectCheckboxTd
                        groupAllSelected={props.dataItem.groupAllSelected}
                        groupSomeSelected={props.dataItem.groupSomeSelected}
                        groupChildren={props.dataItem.groupChildren}
                        setSelectedState={setSelectedState}
                    />
                );
            }
            return (
                <td className="text-center">
                    <div className="d-inline-flex justify-content-center">
                        <Checkbox
                            checked={!!selectedState[props.dataItem.postingId]}
                            onChange={(e) =>
                                setSelectedState((prev) => ({
                                    ...prev,
                                    [props.dataItem.postingId]: e.value,
                                }))
                            }
                        />
                    </div>
                </td>
            );
        },
        [selectedState, setSelectedState],
    );

    const rowRender = useCallback((row, rowProps) => {
        if (rowProps?.dataItem?.isGroupParent) {
            return React.cloneElement(row, {
                style: { ...(row.props.style || {}), backgroundColor: "#f8f8f8" },
            });
        }
        return row;
    }, []);

    const DetailSelectCell = useCallback(
        (props) => (
            <td className="text-center">
                <div className="d-inline-flex justify-content-center">
                    <Checkbox
                        checked={!!selectedState[props.dataItem.postingId]}
                        onChange={(e) =>
                            setSelectedState((prev) => ({
                                ...prev,
                                [props.dataItem.postingId]: e.value,
                            }))
                        }
                    />
                </div>
            </td>
        ),
        [selectedState, setSelectedState],
    );

    const DetailComponent = useCallback(
        (props) => {
            if (!props.dataItem?.isGroupParent) return null;
            const children = props.dataItem.groupChildren || [];
            return (
                <div className={styles.detailGridWrap}>
                    <Grid
                        data={children}
                        className={`${styles.detailNestedGrid} grid-row-select grid-minwidth-col5-150`}
                        scrollable="none"
                        sortable={false}>
                        <Column
                            field="date"
                            title="Date"
                            editable={false}
                            width="110px"
                            cells={detailCells(DateCell)}
                        />
                        <Column
                            field="postingType"
                            title="Type"
                            editable={false}
                            width="80px"
                            cells={detailCells(DetailGridTextCell)}
                        />
                        <Column
                            field="matterRef"
                            title="Matter Ref"
                            editable={false}
                            width="120px"
                            cells={detailCells(MatterRefCell)}
                        />
                        <Column
                            field="reference"
                            title="Ref"
                            editable={false}
                            className="text-wrap bank-rec-ref-min"
                            cells={detailCells(DetailGridTextCell)}
                        />
                        <Column
                            field="details"
                            title="Details"
                            editable={false}
                            cells={detailCells(DetailGridTextCell)}
                        />
                        <Column
                            field="transactionPeriod"
                            title="Period"
                            editable={false}
                            width="80px"
                            cells={detailCells(DetailGridTextCell)}
                        />
                        <Column
                            field="netAmount"
                            title="Receipt"
                            editable={false}
                            width="120px"
                            cells={detailCells(RecieptCell)}
                        />
                        <Column
                            field="netAmount"
                            title="Payment"
                            editable={false}
                            width="120px"
                            cells={detailCells(PaymentCell)}
                        />
                        <Column
                            field={selectField}
                            title="Select"
                            editor="boolean"
                            width="80px"
                            className="text-center"
                            cells={detailCells(DetailSelectCell)}
                        />
                    </Grid>
                </div>
            );
        },
        [DateCell, MatterRefCell, RecieptCell, PaymentCell, DetailSelectCell, selectField],
    );

    return {
        displayedGridData,
        SelectCell,
        rowRender,
        DetailComponent,
    };
}
